import { useTranslation } from 'react-i18next'
import { AppProvider } from '../context/AppContext'
import { Provider } from 'react-redux'

import {
    MuiThemeProvider as MuiV4ThemeProvider,
    makeStyles,
    styled,
} from '@material-ui/core/styles'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
    MutationCache,
    type Query,
    QueryClient,
    QueryClientProvider,
    type QueryKey,
    type QueryObserver,
    matchQuery,
} from '@tanstack/react-query'
import { MaterialDesignContent, SnackbarProvider } from 'notistack'
import { legacy_createStore as createStore } from 'redux'

import { muiV4Theme } from '~/theme-v4'
import { ThemeProvider as MuiV5ThemeProvider } from '~/theme-v5'
import rootReducer from '../oakra/reducers'

import 'dayjs/locale/th'

declare module '@tanstack/react-query' {
    interface MutationMeta {
        invalidates?: QueryKey[]
    }
}

const STALE_TIME = 1000 * 30 // 30 seconds
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: STALE_TIME,
            refetchOnWindowFocus: false,
        },
    },
    mutationCache: new MutationCache({
        onSuccess: (_data, _variables, _context, mutation) => {
            /**
             * Filters out queries that are meant to be static/permanent cached data.
             *
             * When invalidating queries after mutations, we want to avoid invalidating
             * queries that contain reference/lookup data that rarely changes.
             * These queries are marked as static by setting their staleTime to Infinity.
             *
             * This function checks the effective staleTime across all observers of a query
             * to determine if it should be invalidated. If any observer has a finite
             * staleTime, the query is considered non-static.
             *
             * @see https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations#exclude-queries-depending-on-staletime
             */
            function getNonStaticQueries(query: Query): boolean {
                const defaultStaleTime =
                    queryClient.getQueryDefaults(query.queryKey)?.staleTime ?? 0

                // @ts-expect-error should not be an issue once we upgrade to react-query v5
                const observers: QueryObserver[] = query.observers
                const staleTimes = observers
                    .map((observer) => observer.options.staleTime)
                    .filter((staleTime) => staleTime !== undefined)

                const staleTime =
                    query.getObserversCount() > 0 ? Math.min(...staleTimes) : defaultStaleTime

                return staleTime !== Number.POSITIVE_INFINITY
            }

            queryClient.invalidateQueries({
                /**
                 * Invalidates queries after a successful mutation.
                 *
                 * If the mutation has specified invalidation keys in its meta.invalidates array,
                 * only queries matching those keys will be invalidated.
                 *
                 * Otherwise, all non-static queries will be invalidated by default.
                 * Static queries (those with infinite staleTime) are preserved.
                 */
                predicate: (query) => {
                    if (!mutation.meta?.invalidates?.length) {
                        return getNonStaticQueries(query)
                    }

                    return mutation.meta.invalidates.some((queryKey) =>
                        matchQuery({ queryKey }, query)
                    )
                },
            })
        },
    }),
})

const reduxStore = createStore(rootReducer, window.__REDUX_DEVTOOLS_EXTENSION__?.())

const StyledMaterialDesignContent = styled(MaterialDesignContent)(({ theme }) => ({
    '&.notistack-MuiContent-success': {
        backgroundColor: theme.palette.primary.main,
    },
    '&.notistack-MuiContent-error': {
        backgroundColor: theme.palette.error.main,
    },
    '&.notistack-MuiContent-info': {
        backgroundColor: theme.palette.secondary.main,
    },
}))

type Props = {
    children: React.ReactNode
}

const useStyles = makeStyles((theme) => ({
    snackbarRoot: {
        '&:has(#product-detail)': {
            bottom: theme.spacing(8),
        },
    },
}))

export default function Providers({ children }: Props) {
    const classes = useStyles()
    const { i18n } = useTranslation('common', { useSuspense: false })

    return (
        <AppProvider>
            <QueryClientProvider client={queryClient}>
                <Provider store={reduxStore}>
                    <MuiV5ThemeProvider>
                        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterDayjs}>
                            <MuiV4ThemeProvider theme={muiV4Theme}>
                                <SnackbarProvider
                                    classes={{ containerRoot: classes.snackbarRoot }}
                                    Components={{
                                        success: StyledMaterialDesignContent,
                                        error: StyledMaterialDesignContent,
                                        info: StyledMaterialDesignContent,
                                    }}
                                    anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                                >
                                    {children}
                                </SnackbarProvider>
                            </MuiV4ThemeProvider>
                        </LocalizationProvider>
                    </MuiV5ThemeProvider>
                </Provider>
            </QueryClientProvider>
        </AppProvider>
    )
}
